import { Component, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { addDoc, arrayUnion, collection, deleteDoc, doc, docData, serverTimestamp, Timestamp, updateDoc } from '@angular/fire/firestore'
import {
    Client,
    ContractStatus,
    Merchant,
    merchantsPath,
    Parking,
    parkingsPath,
    Plan,
    Subscription,
    SubscriptionEventActor,
    SubscriptionFieldChange,
    subscriptionsPath,
    SubscriptionType,
    Term,
} from '@parkupp/core'
import { AdminService } from '../../../services/admin.service'
import * as moment from 'moment'
import * as firestore from 'firebase/firestore'
import { ActivatedRoute, Router } from '@angular/router'
import { MerchantService } from '../../../services/merchant.service'
import { ParkingService } from '../../../services/parking.service'
import { ClientService } from '../../../services/client.service'
import { SubscriptionService } from '../../../services/subscription.service'
import Swal from 'sweetalert2'
import { AdminChangeLog, ChangeLogsEntityEnum } from '@parkupp/core'
import { AdminChangeLogService } from 'src/app/services/admin-change-log.service'
import { AuthenticationService } from 'src/app/services/authentication.service'
import { EnterpriseContextService } from '../../../services/enterprise-context.service'
import { SubscriptionEventLogService } from '../../../services/subscription-event-log.service'
import { Observable, map, of, tap } from 'rxjs'

type EntryMode = 'manual' | 'linked'

@Component({
    selector: 'app-admin-subscription-manual-create',
    templateUrl: './admin-subscription-manual-create.component.html',
    styleUrls: ['./admin-subscription-manual-create.component.scss'],
})
export class AdminSubscriptionManualCreateComponent implements OnInit {
    SubscriptionType = SubscriptionType
    subscriptionKey: string
    formData: FormGroup
    formLoading: boolean = false
    deleteLoading: boolean = false
    subscription: Subscription
    /** True once we've confirmed the loaded subscription may be edited at all. */
    editAllowed: boolean = false
    /** Guards the "cannot be edited" alert against the live docData stream firing twice. */
    private editBlocked: boolean = false
    merchantLookup: Merchant[] = []
    parkingLookup: Parking[] = []
    clientLookup: any[] = []
    
    // Entry mode toggles
    clientEntryMode: EntryMode = 'manual'
    merchantEntryMode: EntryMode = 'manual'
    parkingEntryMode: EntryMode = 'manual'

    constructor(
        private router: Router,
        private formBuilder: FormBuilder,
        private adminService: AdminService,
        private activatedRoute: ActivatedRoute,
        private merchantService: MerchantService,
        private parkingService: ParkingService,
        private clientService: ClientService,
        private subscriptionService: SubscriptionService,
        private adminChangeLogService: AdminChangeLogService,
        private authService: AuthenticationService,
        private eventLogService: SubscriptionEventLogService,
        public enterpriseContext: EnterpriseContextService
    ) {
        this.subscriptionKey = this.activatedRoute.snapshot.params['subscriptionKey']

        this.formData = this.formBuilder.group({
            createdAtDate: [moment().format('yyyy-MM-DD'), []],
            startDate: [moment().add(1, 'M').startOf('month').format('yyyy-MM-DD'), [Validators.required]],
            endDate: [moment().add(1, 'M').endOf('month').format('yyyy-MM-DD'), []],
            baysBooked: [1, [Validators.required, Validators.min(1)]],
            feeInRands: ['', [Validators.required]],
            contractLength: [1, [Validators.required, Validators.min(1)]],
            // Linked entity fields (optional)
            merchantName: [''],
            merchantKey: [''],
            parkingName: [''],
            parkingKey: [''],
            clientName: [''],
            clientKey: [''],
            // Manual text entry fields
            manualClientName: [''],
            manualMerchantName: [''],
            manualParkingName: [''],
        })

        this.formData.controls['contractLength'].valueChanges.subscribe((value) => this.formChanges({ contractLength: value }))
        this.formData.controls['startDate'].valueChanges.subscribe((value) => this.formChanges({ startDate: value }))

        if (this.subscriptionKey) {
            this.getEditingSubscription(this.subscriptionKey)
        } else {
            this.subscription = new Subscription()
            this.subscription.type = SubscriptionType.MANUAL
            this.editAllowed = true
        }

        // Load merchants (all, regardless of enterprise context)
        this.merchantService.listAll().subscribe((merchants) => {
            this.merchantLookup = merchants
            console.log('[DEBUG] Merchant list loaded:', merchants.length, 'merchants')
        })

        // Load clients with search field
        this.clientService.list().subscribe((clients) => {
            this.clientLookup = clients.map((client) => ({
                ...client,
                searchField: `${client.firstName} ${client.lastName} ${client.companyName}`,
            }))
            console.log('[DEBUG] Client list loaded:', clients.length, 'clients')
        })
    }
    
    // Toggle entry mode for each entity type
    setClientEntryMode(mode: EntryMode): void {
        this.clientEntryMode = mode
        if (mode === 'manual') {
            this.formData.controls['clientKey'].setValue('')
            this.formData.controls['clientName'].setValue('')
        } else {
            this.formData.controls['manualClientName'].setValue('')
        }
    }
    
    setMerchantEntryMode(mode: EntryMode): void {
        this.merchantEntryMode = mode
        if (mode === 'manual') {
            this.formData.controls['merchantKey'].setValue('')
            this.formData.controls['merchantName'].setValue('')
            this.formData.controls['parkingKey'].setValue('')
            this.formData.controls['parkingName'].setValue('')
            this.parkingLookup = []
        } else {
            this.formData.controls['manualMerchantName'].setValue('')
        }
    }
    
    setParkingEntryMode(mode: EntryMode): void {
        this.parkingEntryMode = mode
        if (mode === 'manual') {
            this.formData.controls['parkingKey'].setValue('')
            this.formData.controls['parkingName'].setValue('')
        } else {
            this.formData.controls['manualParkingName'].setValue('')
        }
    }
    
    // Check if form has valid client entry (either manual or linked)
    hasValidClientEntry(): boolean {
        if (this.clientEntryMode === 'manual') {
            return this.formData.value.manualClientName?.trim()?.length > 0
        }
        return this.formData.value.clientKey?.length > 0
    }
    
    // Check if form has valid merchant entry (either manual or linked)
    hasValidMerchantEntry(): boolean {
        if (this.merchantEntryMode === 'manual') {
            return this.formData.value.manualMerchantName?.trim()?.length > 0
        }
        return this.formData.value.merchantKey?.length > 0
    }
    
    // Check if form has valid parking entry (either manual or linked)
    hasValidParkingEntry(): boolean {
        if (this.parkingEntryMode === 'manual') {
            return this.formData.value.manualParkingName?.trim()?.length > 0
        }
        return this.formData.value.parkingKey?.length > 0
    }
    
    // Check if entire form is valid
    isFormValid(): boolean {
        return this.formData.valid && 
               this.hasValidClientEntry() && 
               this.hasValidMerchantEntry() && 
               this.hasValidParkingEntry()
    }

    getParkings(merchantKey: string) {
        const merchantRef = doc(this.adminService.firestore, merchantsPath(), merchantKey)
        this.parkingService.listByMerchantRef(merchantRef).subscribe((parkings) => {
            this.parkingLookup = parkings
        })
    }

    getEditingSubscription(subscriptionKey: string) {
        docData(doc(this.adminService.firestore, subscriptionsPath(), subscriptionKey)).subscribe((documentData: any) => {
            this.subscription = new Subscription(documentData)
            this.subscription.$key = subscriptionKey

            // A PayGate/PayFast subscription is backed by a live payment mandate the parker
            // signed up to, so its terms are not ours to rewrite. Bounce straight back to the
            // read-only view rather than showing a form that can't legitimately be saved.
            if (!this.subscription.isAdminEditable) {
                if (!this.editBlocked) {
                    this.editBlocked = true
                    Swal.fire({
                        title: 'This subscription cannot be edited',
                        text: `This is a ${this.subscription.paymentMechanismName} subscription — an active lease agreement backed by a payment mandate. Only EFT and manual subscriptions can be edited.`,
                        icon: 'info',
                        confirmButtonText: 'View Subscription',
                    }).then(() => {
                        this.router.navigate(['/admin/subscription', subscriptionKey])
                    })
                }
                return
            }

            this.editAllowed = true

            this.formData.patchValue({
                createdAtDate: this.subscription.createdAt ? moment(this.subscription.createdAt.toDate()).format('yyyy-MM-DD') : moment().format('yyyy-MM-DD'),
                startDate: moment(this.subscription.startDate.toDate()).format('yyyy-MM-DD'),
                endDate: moment(this.subscription.endDate.toDate()).format('yyyy-MM-DD'),
                baysBooked: this.subscription.baysBooked,
                feeInRands: this.subscription.plan?.feeInCents ? this.subscription.plan.feeInCents / 100 : 0,
                contractLength: this.subscription.period,
            })

            // Load client - check if linked or manual
            if (this.subscription.clientRef) {
                this.clientEntryMode = 'linked'
                this.formData.controls['clientKey'].setValue(this.subscription.clientRef.id)
            } else if (this.subscription.manualClientName) {
                this.clientEntryMode = 'manual'
                this.formData.controls['manualClientName'].setValue(this.subscription.manualClientName)
            }

            // Load merchant - check if linked or manual
            if (this.subscription.merchantRef) {
                this.merchantEntryMode = 'linked'
                this.formData.controls['merchantKey'].setValue(this.subscription.merchantRef.id)
                this.getParkings(this.subscription.merchantRef.id)
            } else if (this.subscription.manualMerchantName) {
                this.merchantEntryMode = 'manual'
                this.formData.controls['manualMerchantName'].setValue(this.subscription.manualMerchantName)
            }

            // Load parking - check if linked or manual
            if (this.subscription.parkingRef) {
                this.parkingEntryMode = 'linked'
                this.formData.controls['parkingKey'].setValue(this.subscription.parkingRef.id)
            } else if (this.subscription.manualParkingName) {
                this.parkingEntryMode = 'manual'
                this.formData.controls['manualParkingName'].setValue(this.subscription.manualParkingName)
            }
        })
    }

    ngOnInit(): void {
        console.log('[DEBUG] AdminSubscriptionManualCreateComponent initialized')
    }

    formChanges(changes: { contractLength?: number; startDate?: number }) {
        let contractLength = changes.contractLength ? changes.contractLength : this.formData.value.contractLength
        let startDate = changes.startDate ? changes.startDate : this.formData.value.startDate

        if (changes.contractLength || changes.startDate) {
            this.updateEndDate(Term.MONTHLY, startDate, contractLength)
        }
    }

    updateEndDate(term: string, startDate: Date, contractLength: any) {
        let lengthUnit = 'M'
        if (term == Term.MONTHLY) {
            lengthUnit = 'M'
        }
        if (term == Term.DAILY) {
            lengthUnit = 'd'
        }
        if (term == Term.ONCEOFF) {
            lengthUnit = 'd'
        }

        const newEndDate = moment(startDate).add(contractLength, lengthUnit).subtract(1, 'day')
        this.formData.controls['endDate'].setValue(newEndDate.format('yyyy-MM-DD'))
    }

    async onSubmit() {
        if (!this.editAllowed) {
            return
        }

        this.formLoading = true

        // Formatted "before" values, read before any in-place mutation below.
        const beforeSnapshot = this.eventLogService.snapshot(this.subscription)

        // Use the created date from the form (or current date if not set)
        if (this.formData.value.createdAtDate) {
            this.subscription.createdAt = firestore.Timestamp.fromDate(new Date(this.formData.value.createdAtDate))
        } else if (!this.subscription.$key) {
            this.subscription.createdAt = serverTimestamp() as firestore.Timestamp
        }
        this.subscription.updatedAt = serverTimestamp() as firestore.Timestamp

        const plan = new Plan({
            feeInCents: this.formData.value.feeInRands * 100,
            term: Term.MONTHLY,
        })

        // The payment mechanism is fixed at creation. New records off this page are manual;
        // an existing record keeps whatever it was created with, so editing an EFT
        // subscription never silently downgrades it to a manual one.
        if (!this.subscription.$key) {
            this.subscription.type = SubscriptionType.MANUAL
        }

        // Manual subscriptions track external agreements, so they must never be invoiced.
        // EFT subscriptions are invoiced by us, so leave their debit schedule alone.
        if (this.subscription.type === SubscriptionType.MANUAL) {
            this.subscription.nextDebitDate = null
        }

        this.subscription.baysBooked = this.formData.value.baysBooked
        this.subscription.startDate = firestore.Timestamp.fromDate(new Date(this.formData.value.startDate))
        this.subscription.endDate = firestore.Timestamp.fromDate(new Date(this.formData.value.endDate))
        if (!this.subscription.$key) {
            this.subscription.status = ContractStatus.active
        }
        this.subscription.plan = plan
        this.subscription.period = this.formData.value.contractLength

        // Handle client - either linked or manual
        if (this.clientEntryMode === 'linked' && this.formData.value.clientKey) {
            const clientRef = this.clientService.getDocRef(this.formData.value.clientKey)
            this.subscription.clientRef = clientRef as any
            this.subscription.manualClientName = ''

            const client = await this.clientService.getClientByUid(this.formData.value.clientKey)
            if (client) {
                this.subscription.denormClientFirstName = client.firstName
                this.subscription.denormClientLastName = client.lastName
            }
        } else {
            // Manual client entry - no clientRef (prevents invoice generation)
            this.subscription.clientRef = null as any
            this.subscription.manualClientName = this.formData.value.manualClientName
            this.subscription.denormClientFirstName = ''
            this.subscription.denormClientLastName = ''
        }

        // Handle merchant - either linked or manual
        if (this.merchantEntryMode === 'linked' && this.formData.value.merchantKey) {
            const merchantRef = this.merchantService.getDocRef(this.formData.value.merchantKey)
            this.subscription.merchantRef = merchantRef as any
            this.subscription.manualMerchantName = ''

            const merchant: Merchant = await this.merchantService.getMerchant(this.formData.value.merchantKey)
            this.subscription.denormMerchantCompanyName = merchant.companyName
        } else {
            // Manual merchant entry
            this.subscription.merchantRef = null as any
            this.subscription.manualMerchantName = this.formData.value.manualMerchantName
            this.subscription.denormMerchantCompanyName = ''
        }

        // Handle parking - either linked or manual
        if (this.parkingEntryMode === 'linked' && this.formData.value.parkingKey) {
            const parkingRef = this.parkingService.getDocRef(this.formData.value.parkingKey)
            this.subscription.parkingRef = parkingRef as any
            this.subscription.manualParkingName = ''

            const parking: Parking = await this.parkingService.getStatic(this.formData.value.parkingKey)
            this.subscription.denormParkingTitle = parking.title
            
            // Inherit enterprise from parking
            if (parking.denormEnterpriseKey) {
                this.subscription.denormEnterpriseKey = parking.denormEnterpriseKey
            }
            if (parking.isMonthToMonth) {
                this.subscription.isMonthToMonth = true
            }
        } else {
            // Manual parking entry
            this.subscription.parkingRef = null as any
            this.subscription.manualParkingName = this.formData.value.manualParkingName
            this.subscription.denormParkingTitle = ''
            
            // Set enterprise from current context for manual entries
            if (this.enterpriseContext.currentEnterpriseKey) {
                this.subscription.denormEnterpriseKey = this.enterpriseContext.currentEnterpriseKey
            }
        }

        if (this.subscription.$key) {
            const payload = this.subscription.parse()
            // The event log is only ever appended to via arrayUnion — writing our in-memory
            // copy back would drop anything logged since this page loaded.
            delete payload.eventLog

            // The audit entry rides along in the same write as the change it describes, so
            // there is no window in which the edit exists unattributed.
            const changes = this.eventLogService.diffSnapshots(beforeSnapshot, this.eventLogService.snapshot(this.subscription))
            const auditEvent = await this.buildAuditEvent(changes)
            if (auditEvent) {
                payload.eventLog = arrayUnion(auditEvent)
            }

            await updateDoc(doc(this.adminService.firestore, subscriptionsPath(), this.subscription.$key), payload)
            await this.logSubscriptionUpdateToChangeLog(changes)
            this.formLoading = false
            this.router.navigate([`/admin/subscriptions`])
        } else {
            await addDoc(collection(this.adminService.firestore, subscriptionsPath()), this.subscription.parse())
            this.formLoading = false
            this.router.navigate([`/admin/subscriptions`])
        }
    }
    
    /**
     * Hard delete is only for manual records, which exist purely as bookkeeping. An EFT
     * subscription is a real lease and must be cancelled, not erased.
     */
    get canDelete(): boolean {
        return !!this.subscription?.$key && this.subscription.type === SubscriptionType.MANUAL
    }

    async deleteSubscription() {
        if (!this.canDelete) return

        const result = await Swal.fire({
            title: 'Delete Manual Subscription?',
            text: 'This action cannot be undone. Are you sure you want to delete this subscription?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete it',
            cancelButtonText: 'Cancel'
        })
        
        if (result.isConfirmed) {
            this.deleteLoading = true
            try {
                // Logged before the delete — the subscription's own event log dies with the
                // document, so the admin change log is the only surviving record.
                await this.logSubscriptionDeletion()
                await deleteDoc(doc(this.adminService.firestore, subscriptionsPath(), this.subscription.$key!))
                await Swal.fire({
                    title: 'Deleted!',
                    text: 'The manual subscription has been deleted.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                })
                this.router.navigate(['/admin/subscriptions'])
            } catch (error) {
                console.error('Error deleting subscription:', error)
                await Swal.fire({
                    title: 'Error',
                    text: 'Failed to delete the subscription. Please try again.',
                    icon: 'error'
                })
            } finally {
                this.deleteLoading = false
            }
        }
    }

    private async logSubscriptionDeletion() {
        const user = await this.authService.waitForUid()
        const admin = await this.clientService.getClientByUid(user.uid)
        if (!admin) {
            return
        }

        const adminName = `${admin.firstName || ''} ${admin.lastName || ''}`.trim() || 'Admin'
        const changeLog = new AdminChangeLog({
            updatedEntity: ChangeLogsEntityEnum.SUBSCRIPTION,
            subscriptionRef: this.subscriptionService.getDocRef(this.subscription.$key!) as any,
            adminRef: this.clientService.getDocRef(admin.$key!) as any,
            comment: `Manual subscription deleted by ${adminName} (${this.subscription.denormParkingTitle || this.subscription.manualParkingName || 'no parking'}, ${this.subscription.denormClientFirstName || this.subscription.manualClientName || 'no client'})`,
            createdAt: serverTimestamp() as Timestamp,
        })
        await this.adminChangeLogService.addOrUpdate(changeLog)
    }

    /** The timeline entry for this edit, or null when nothing actually changed. */
    private async buildAuditEvent(changes: SubscriptionFieldChange[]): Promise<Record<string, any> | null> {
        if (changes.length === 0) {
            return null
        }

        const user = await this.authService.waitForUid()
        const admin = await this.clientService.getClientByUid(user.uid)
        const adminName = `${admin?.firstName || ''} ${admin?.lastName || ''}`.trim() || 'Admin'

        return this.eventLogService.buildChangeEvent(SubscriptionEventActor.ADMIN, changes, {
            source: 'admin-portal',
            actorDetails: {
                name: adminName,
                adminKey: user.uid,
                ...(this.authService.email ? { email: this.authService.email } : {}),
            },
            summary: `${adminName} edited the subscription: ${this.describeChanges(changes)}`,
        })
    }

    /** Mirror the edit into the global admin change log so it shows up in cross-entity views. */
    async logSubscriptionUpdateToChangeLog(changes: SubscriptionFieldChange[]) {
        if (changes.length === 0) {
            return
        }

        const user = await this.authService.waitForUid()
        const admin = await this.clientService.getClientByUid(user.uid)
        const adminName = `${admin?.firstName || ''} ${admin?.lastName || ''}`.trim() || 'Admin'

        if (admin) {
            const adminRef = this.clientService.getDocRef(admin.$key!)
            const subscriptionRef = this.subscriptionService.getDocRef(this.subscription.$key!)

            const changeLog = new AdminChangeLog({
                updatedEntity: ChangeLogsEntityEnum.SUBSCRIPTION,
                subscriptionRef: subscriptionRef as any,
                adminRef: adminRef as any,
                comment: `Subscription edited by ${adminName}: ${this.describeChanges(changes)}`,
                createdAt: serverTimestamp() as Timestamp,
            })
            await this.adminChangeLogService.addOrUpdate(changeLog)
        }
    }

    private describeChanges(changes: { label: string; from: string; to: string }[]): string {
        return changes.map((change) => `${change.label} ${change.from} → ${change.to}`).join('; ')
    }

    onMerchantSelect(selectedMerchant: Merchant): void {
        if (selectedMerchant) {
            this.formData.controls['merchantKey'].setValue(selectedMerchant.$key)
            this.getParkings(selectedMerchant.$key!)
        }
        this.formData.controls['parkingName'].setValue('')
        this.formData.controls['parkingKey'].setValue('')
    }

    onParkingSelect(event: any): void {
        if (event) {
            const parkingKey = event.target.value
            this.formData.controls['parkingKey'].setValue(parkingKey)
        }
    }

    onClientSelect(selectedClient: Client): void {
        if (selectedClient) {
            this.formData.controls['clientKey'].setValue(selectedClient.$key)
        }
    }

    isMerchantSelected(): boolean {
        return this.formData.value.merchantKey !== ''
    }
}
